/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronRight,
  Clock,
  BookOpen,
  CheckCircle2,
  FileText,
  Video,
  Link as LinkIcon,
  ExternalLink,
  MoreHorizontal,
  RotateCcw,
  AlertTriangle,
  XCircle,
  MessageSquare,
  Flag,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { Progress } from '../../../../components/ui/progress';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import GoalsConstants from '../../../../constants/goals.constants';
import { cn } from '../../../../lib/utils';
import { mergeSimilarSkillDetails } from '../../../Head/helpers/reviews.helpers';
import ReportIssueModal from '../../v2/components/ReportIssueModal';

const materialIcons = {
  video: Video,
  article: FileText,
  book: BookOpen,
  link: LinkIcon,
};

function LessonItem({
  detail,
  skill,
  allDetails,
  onLinkClick,
  onStatusChange,
  onReportIssue,
  isDisabled,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Get all materials for this lesson (details with the same name)
  const lessonMaterials = useMemo(() => {
    if (!allDetails) return [];
    return allDetails
      .filter((d) => d.name === detail.name && (d.materialName || d.link))
      .map((d) => ({
        itemId: d.itemId,
        name: d.materialName || d.name,
        link: d.link,
        type: d.materialType,
      }));
  }, [allDetails, detail.name]);

  const MaterialIcon = materialIcons[detail.materialType?.toLowerCase()] || FileText;
  const isInProgress = detail.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS;
  const isDone = detail.status === GoalsConstants.GOAL_STATUSES.DONE;
  const isApproved = detail.status === GoalsConstants.GOAL_STATUSES.APPROVED;
  const isDeclined = detail.status === GoalsConstants.GOAL_STATUSES.DECLINED;
  const isCompleted = isDone || isApproved;

  const canShowMenu = !isApproved;

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleDone = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onStatusChange({
      parentId: skill.itemId,
      itemId: detail.itemId,
      status: GoalsConstants.GOAL_STATUSES.DONE,
    });
  };

  const handleInProgress = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onStatusChange({
      parentId: skill.itemId,
      itemId: detail.itemId,
      status: GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
    });
  };

  const handleReportIssue = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    // Pass materials array to the report modal
    const detailWithMaterials = {
      ...detail,
      materials: lessonMaterials,
    };
    onReportIssue?.({ skill, detail: detailWithMaterials });
  };

  const getStatusLabel = () => {
    if (isDeclined) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="h-3 w-3" />
          Declined
        </span>
      );
    }
    if (isCompleted) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          Done
        </span>
      );
    }
    if (isInProgress) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          Learning
        </span>
      );
    }
    return null;
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-4 rounded-xl bg-card transition-all group',
        'hover:shadow-sm',
        isCompleted && 'bg-muted/30',
        isDeclined && 'bg-red-50/50',
      )}
      style={{
        border: isDeclined ? '1px solid #fecaca' : '1px solid hsl(var(--border))',
      }}
    >
      <div className="flex items-start gap-4">
        {/* Lesson Info */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className={cn('font-semibold text-base', isCompleted && 'text-muted-foreground')}>
            {detail.name}
          </p>

          {/* Details List */}
          <ul className="mt-2 space-y-1.5 text-sm">
            {/* Criteria */}
            {detail.successCriteria && (
              <li className="text-gray-600">
                <span className="font-medium text-gray-700">Criteria:</span>
                <br />
                <span className="font-semibold pl-3">{detail.successCriteria}</span>
              </li>
            )}

            {/* Materials */}
            {lessonMaterials.length > 0 && (
              <li className="text-gray-600">
                <span className="font-medium text-gray-700">Materials:</span>
                <ul className="pl-3 mt-1 space-y-1">
                  {lessonMaterials.map((material) => {
                    const MatIcon = materialIcons[material.type?.toLowerCase()] || FileText;
                    return (
                      <li key={material.itemId || material.name}>
                        <a
                          href={material.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLinkClick?.({ taskId: material.itemId, link: material.link });
                          }}
                        >
                          <MatIcon className="h-3.5 w-3.5" />
                          {material.name}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}
          </ul>
        </div>

        {/* Status Label */}
        <div className="shrink-0 mt-0.5">{getStatusLabel()}</div>

        {/* Actions Menu */}
        {canShowMenu && (
          <div className="relative" ref={menuRef}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleMenuClick}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>

            {menuOpen && (
              <div className="absolute right-0 top-8 z-50 min-w-[140px] rounded-md border bg-popover p-1 shadow-md">
                {!isDisabled && (isInProgress || isDeclined) && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={handleDone}
                  >
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Mark as Done
                  </button>
                )}
                {!isDisabled && (isDone || isDeclined) && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={handleInProgress}
                  >
                    <RotateCcw className="h-4 w-4 text-amber-500" />
                    Back to Progress
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-red-600"
                  onClick={handleReportIssue}
                >
                  <Flag className="h-4 w-4" />
                  Report Issue
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Decline Reason */}
      {isDeclined && detail.comment && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-100/50 border border-red-200">
          <MessageSquare className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-red-700 mb-0.5">Decline Reason:</p>
            <p className="text-sm text-red-600">{detail.comment}</p>
          </div>
        </div>
      )}
    </div>
  );
}

LessonItem.propTypes = {
  detail: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
    successCriteria: PropTypes.string,
    materialName: PropTypes.string,
    materialType: PropTypes.string,
    link: PropTypes.string,
    status: PropTypes.string,
    duration: PropTypes.string,
    comment: PropTypes.string,
  }).isRequired,
  skill: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  allDetails: PropTypes.array,
  onLinkClick: PropTypes.func,
  onStatusChange: PropTypes.func.isRequired,
  onReportIssue: PropTypes.func,
  isDisabled: PropTypes.bool,
};

function SkillDetailView({ skill, allSkills, onLinkClick, onDetailStatusChange, isDisabled }) {
  const [reportIssueModal, setReportIssueModal] = useState({
    isOpen: false,
    skill: null,
    detail: null,
  });

  const handleReportIssue = ({ skill: reportSkill, detail }) => {
    setReportIssueModal({
      isOpen: true,
      skill: reportSkill,
      detail,
    });
  };

  const handleCloseReportModal = () => {
    setReportIssueModal({
      isOpen: false,
      skill: null,
      detail: null,
    });
  };

  // Get parent chain for breadcrumb
  const breadcrumbs = useMemo(() => {
    if (!skill) return [];

    const parents = [];
    const findParent = (itemId) => {
      const parent = allSkills.find((s) => s.children?.includes(itemId));
      if (parent) {
        parents.unshift(parent);
        findParent(parent.itemId);
      }
    };

    findParent(skill.itemId);
    return parents;
  }, [skill, allSkills]);

  // Get merged details (lessons)
  const lessons = useMemo(() => {
    if (!skill?.details) return [];
    return mergeSimilarSkillDetails(skill.details);
  }, [skill]);

  // Calculate progress
  const progressData = useMemo(() => {
    if (!lessons.length) return { completed: 0, total: 0, percentage: 0 };

    const completed = lessons.filter(
      (l) =>
        l.status === GoalsConstants.GOAL_STATUSES.DONE ||
        l.status === GoalsConstants.GOAL_STATUSES.APPROVED,
    ).length;

    return {
      completed,
      total: lessons.length,
      percentage: Math.round((completed / lessons.length) * 100),
    };
  }, [lessons]);

  // Calculate total estimated time
  const totalDuration = useMemo(() => {
    // Mock calculation - in real app, this would come from data
    return `${lessons.length * 15} min`;
  }, [lessons]);

  // Empty state
  if (!skill) {
    return (
      <Card className="h-full flex flex-col items-center justify-center">
        <CardContent className="text-center py-16">
          <div className="rounded-full bg-muted p-6 mb-6 inline-block">
            <BookOpen className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Select a skill</h3>
          <p className="text-muted-foreground max-w-sm">
            Choose a skill from the tree on the left to see its details, lessons, and track your
            progress.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      {/* Fixed Header Section */}
      <div className="p-6 pb-0 shrink-0">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
          {breadcrumbs.map((parent) => (
            <React.Fragment key={parent.itemId}>
              <span className="hover:text-foreground cursor-default">{parent.name}</span>
              <ChevronRight className="h-4 w-4" />
            </React.Fragment>
          ))}
          <span className="text-foreground font-medium">{skill.name}</span>
        </nav>

        {/* Skill Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-3">{skill.name}</h1>

          {skill.description && <p className="text-muted-foreground mb-4">{skill.description}</p>}

          {/* Meta info */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{totalDuration}</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span>{lessons.length} lessons</span>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="mb-6 p-4 rounded-xl bg-muted/30 border">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">Progress</span>
            <span className="text-sm text-muted-foreground">{progressData.percentage}%</span>
          </div>
          <Progress value={progressData.percentage} className="h-2" />
          <p className="text-sm text-muted-foreground mt-2">
            {progressData.completed} of {progressData.total} lessons completed
          </p>
        </div>

        {/* Lessons Header */}
        <h2 className="text-lg font-semibold mb-4">Lessons</h2>
      </div>

      {/* Scrollable Lessons List */}
      <div className="flex-1 min-h-0 overflow-hidden px-6 pb-6">
        <ScrollArea className="h-full overflow-y-auto">
          {lessons.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No lessons available for this skill.</p>
            </div>
          ) : (
            <div className="space-y-3 pr-4">
              {lessons.map((lesson) => (
                <LessonItem
                  key={lesson.itemId || lesson.name}
                  detail={lesson}
                  skill={skill}
                  allDetails={skill.details}
                  onLinkClick={onLinkClick}
                  onStatusChange={onDetailStatusChange}
                  onReportIssue={handleReportIssue}
                  isDisabled={isDisabled}
                />
              ))}

              {/* Recommendations/Comments */}
              {(skill.recommendation || skill.comment) && (
                <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-amber-800 mb-1">Feedback</h3>
                      <p className="text-sm text-amber-700">
                        {skill.recommendation || skill.comment}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Report Issue Modal */}
      {reportIssueModal.skill && reportIssueModal.detail && (
        <ReportIssueModal
          isOpen={reportIssueModal.isOpen}
          onClose={handleCloseReportModal}
          skill={reportIssueModal.skill}
          detail={reportIssueModal.detail}
        />
      )}
    </Card>
  );
}

SkillDetailView.propTypes = {
  skill: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    details: PropTypes.array,
    skillType: PropTypes.string,
    status: PropTypes.string,
    recommendation: PropTypes.string,
    comment: PropTypes.string,
  }),
  allSkills: PropTypes.array,
  onLinkClick: PropTypes.func,
  onDetailStatusChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

SkillDetailView.defaultProps = {
  skill: null,
  allSkills: [],
  onLinkClick: () => {},
  isDisabled: false,
};

export default SkillDetailView;
